/**
 * Weekly To-Do List - Interactive Task Manager
 * Handles task creation, completion tracking, and progress visualization
 */

(function () {
    'use strict';

    let tasks = [];
    let taskIdCounter = 0;

    /**
     * Initialize the weekly to-do list
     */
    function init() {
        // Restore scroll position if returning from form submission
        restoreScrollPosition();

        // Save scroll position before form submissions (only main form, not modal forms)
        var form = document.getElementById('weeklyTodoForm');
        if (form) {
            form.addEventListener('submit', saveScrollPosition);
        }

        initClearConfirmation();

        // Delegate add-task button clicks via data-day-id
        document.addEventListener('click', function (e) {
            var addBtn = e.target.closest('[data-day-id]');
            if (addBtn) {
                addTaskToDay(addBtn.getAttribute('data-day-id'));
                return;
            }

            var deleteBtn = e.target.closest('[data-delete-task-id]');
            if (deleteBtn) {
                deleteTask(parseInt(deleteBtn.getAttribute('data-delete-task-id'), 10));
                return;
            }
        });

        // Delegate checkbox toggle via data-task-id
        document.addEventListener('change', function (e) {
            var checkbox = e.target.closest('[data-task-id]');
            if (checkbox) {
                toggleTask(parseInt(checkbox.getAttribute('data-task-id'), 10));
            }
        });

        // Confirm clear button
        var confirmClearBtn = document.getElementById('btn-confirm-clear');
        if (confirmClearBtn) {
            confirmClearBtn.addEventListener('click', clearAll);
        }

        // Set print date (generated date)
        const printDate = document.getElementById('printDate');
        if (printDate) {
            printDate.textContent = 'Generated on ' + new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric'
            });
        }

        // Load tasks from localStorage
        loadTasksFromStorage();

        // Initial render
        renderTasks();
    }

    /**
     * Load tasks from localStorage
     */
    function loadTasksFromStorage() {
        try {
            const stored = localStorage.getItem('weeklyTodoListTasks');
            if (stored) {
                const data = JSON.parse(stored);
                tasks = data.tasks || [];
                taskIdCounter = data.counter || 0;
            }
        } catch (e) {
            console.error('Failed to load tasks from localStorage:', e);
        }
    }

    /**
     * Save tasks to localStorage
     */
    function saveTasksToStorage() {
        try {
            localStorage.setItem('weeklyTodoListTasks', JSON.stringify({
                tasks: tasks,
                counter: taskIdCounter
            }));
        } catch (e) {
            console.error('Failed to save tasks to localStorage:', e);
        }
    }

    /**
     * Add a task to a specific day
     * @param {string} day - The day identifier (e.g., 'monday', 'tuesday')
     */
    function addTaskToDay(day) {
        const input = document.getElementById('input-' + day);
        const text = input.value.trim();

        if (!text) {
            input.focus();
            return;
        }

        const task = {
            id: ++taskIdCounter,
            text: text,
            day: day,
            priority: document.getElementById('priority-' + day).value,
            category: document.getElementById('category-' + day).value,
            completed: false
        };

        tasks.push(task);
        saveTasksToStorage();
        renderTasks();

        // Clear input and refocus
        input.value = '';
        input.focus();
    }

    /**
     * Toggle task completion status
     * @param {number} id - The task ID
     */
    function toggleTask(id) {
        const task = tasks.find(function (t) {
            return t.id === id;
        });
        if (task) {
            task.completed = !task.completed;
            saveTasksToStorage();
            renderTasks();
        }
    }

    /**
     * Delete a task
     * @param {number} id - The task ID
     */
    function deleteTask(id) {
        tasks = tasks.filter(function (t) {
            return t.id !== id;
        });
        saveTasksToStorage();
        renderTasks();
    }

    /**
     * Render all tasks for each day
     */
    function renderTasks() {
        const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

        days.forEach(function (day) {
            const container = document.getElementById('tasks-' + day);
            const dayTasks = tasks.filter(function (t) {
                return t.day === day;
            });
            const count = document.getElementById('count-' + day);

            const completedCount = dayTasks.filter(function (t) {
                return t.completed;
            }).length;
            count.textContent = completedCount + '/' + dayTasks.length + ' done';

            if (dayTasks.length === 0) {
                container.innerHTML = '<div class="empty-state">No tasks yet</div>';
            } else {
                container.innerHTML = dayTasks.map(function (task) {
                    return createTaskHTML(task);
                }).join('');
            }
        });

        updateProgress();
    }

    /**
     * Create HTML markup for a task
     * @param {Object} task - The task object
     * @returns {string} HTML string for the task
     */
    function createTaskHTML(task) {
        const escapedText = task.text.replace(/</g, '&lt;').replace(/>/g, '&gt;');
        const completedClass = task.completed ? ' task-item--completed' : '';
        const checkedAttr = task.completed ? ' checked' : '';
        const priorityClass = 'task-item--priority-' + task.priority;

        return '<div class="task-item ' + priorityClass + completedClass + '">' +
            '<div class="task-item__checkbox">' +
            '<input type="checkbox"' + checkedAttr + ' data-task-id="' + task.id + '" />' +
            '</div>' +
            '<div class="task-item__content">' +
            '<span class="task-item__text">' + escapedText + '</span>' +
            '</div>' +
            '<div class="task-item__meta">' +
            '<span class="category-badge category-badge--' + task.category + '">' +
            task.category.charAt(0).toUpperCase() + task.category.slice(1) +
            '</span>' +
            '<span class="priority-badge priority-badge--' + task.priority + '">' +
            '<span class="priority-badge__dot"></span>' +
            task.priority.charAt(0).toUpperCase() + task.priority.slice(1) +
            '</span>' +
            '</div>' +
            '<button type="button" class="task-item__delete" data-delete-task-id="' + task.id + '" title="Delete task">' +
            '<i class="bi bi-x-lg" aria-hidden="true"></i>' +
            '</button>' +
            '</div>';
    }

    /**
     * Update the progress bar and text
     */
    function updateProgress() {
        const total = tasks.length;
        const completed = tasks.filter(function (t) {
            return t.completed;
        }).length;
        const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

        const progressBar = document.getElementById('progressBar');
        const progressContainer = progressBar.parentElement;

        progressBar.style.setProperty('--progress', percentage + '%');
        progressContainer.setAttribute('aria-valuenow', percentage);
        document.getElementById('progressText').textContent = completed + ' of ' + total + ' tasks completed (' + percentage + '%)';
    }

    /**
     * Save current scroll position to sessionStorage before form submission
     * Skips saving if the confirmation modal is visible to preserve the original position
     */
    function saveScrollPosition() {
        const modalOverlay = document.querySelector('.clear-confirmation-overlay.is-visible');
        if (modalOverlay) {
            return;
        }

        try {
            sessionStorage.setItem('weeklyTodoListScrollPos', window.pageYOffset.toString());
        } catch (e) {
            // Ignore storage errors
        }
    }

    /**
     * Restore scroll position from sessionStorage after page load
     * Only removes saved position when modal is not visible (to support Confirm/Cancel flow)
     */
    function restoreScrollPosition() {
        try {
            const scrollPos = sessionStorage.getItem('weeklyTodoListScrollPos');
            if (scrollPos) {
                window.scrollTo(0, parseInt(scrollPos, 10));
                
                const modalOverlay = document.querySelector('.clear-confirmation-overlay.is-visible');
                if (!modalOverlay) {
                    sessionStorage.removeItem('weeklyTodoListScrollPos');
                }
            }
        } catch (e) {
            // Ignore storage errors
        }
    }

    /**
     * Initialize clear confirmation modal controls
     */
    function initClearConfirmation() {
        const clearButton = document.getElementById('weeklyTodoListClear');
        const overlay = document.getElementById('clearConfirmationOverlay');
        if (!clearButton || !overlay) {
            return;
        }

        const closeButtons = overlay.querySelectorAll('[data-clear-confirmation="close"]');

        clearButton.addEventListener('click', function () {
            openClearConfirmation(overlay);
        });

        closeButtons.forEach(function (button) {
            button.addEventListener('click', function () {
                closeClearConfirmation(overlay);
            });
        });
    }

    /**
     * Open the clear confirmation modal
     * @param {HTMLElement} overlay The modal overlay element
     */
    function openClearConfirmation(overlay) {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        if (scrollbarWidth > 0) {
            document.body.style.paddingRight = scrollbarWidth + 'px';
        }
        overlay.classList.add('is-visible');
        overlay.setAttribute('aria-hidden', 'false');
        document.body.classList.add('clear-confirmation-open');
    }

    /**
     * Close the clear confirmation modal
     * @param {HTMLElement} overlay The modal overlay element
     */
    function closeClearConfirmation(overlay) {
        overlay.classList.remove('is-visible');
        overlay.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('clear-confirmation-open');
        document.body.style.paddingRight = '';
    }

    /**
     * Clear all tasks
     */
    function clearAll() {
        if (tasks.length === 0) {
            return;
        }
        tasks = [];
        taskIdCounter = 0;
        localStorage.removeItem('weeklyTodoListTasks');
        renderTasks();
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    // Expose public API
    window.WeeklyTodoList = {
        addTaskToDay: addTaskToDay,
        toggleTask: toggleTask,
        deleteTask: deleteTask,
        clearAll: clearAll
    };

})();
